import { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { ExternalLink } from 'lucide-react'
import { Button, Card, LoadingSpinner } from '@/components/ui'

// Constants
const IMAGE_COUNT = 78
const ROTATION_INTERVAL = 15000 // 15 seconds
const IMAGE_CROSSFADE_MS = 2000 // 2s crossfade
const CREDIT_FADE_MS = 500 // 0.5s credit fade
const CREDIT_SETTLE_DELAY_MS = 300 // pause after image settles

// Generate array of background images (bg-01.webp through bg-78.webp)
const BACKGROUND_IMAGES = Array.from({ length: IMAGE_COUNT }, (_, i) =>
  `/backgrounds/bg-${(i + 1).toString().padStart(2, '0')}.webp`
)

// Storage key for persisting shuffle state
const SHUFFLE_STORAGE_KEY = 'login_bg_shuffle'

// Credit metadata type
interface CreditInfo {
  description: string
  url: string
}

// Shuffle state persisted to localStorage
interface ShuffleState {
  queue: string[] // remaining images to show
  version: number // to invalidate if image count changes
}

// Fisher-Yates shuffle
function shuffleArray<T>(array: T[]): T[] {
  const result = [...array]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

// Get or create shuffle state from localStorage
function getShuffleState(): ShuffleState {
  try {
    const stored = localStorage.getItem(SHUFFLE_STORAGE_KEY)
    if (stored) {
      const state = JSON.parse(stored) as ShuffleState
      // Validate version matches current image count
      if (state.version === IMAGE_COUNT && state.queue.length > 0) {
        return state
      }
    }
  } catch {
    // Ignore parse errors
  }

  // Create new shuffled queue
  return {
    queue: shuffleArray(BACKGROUND_IMAGES),
    version: IMAGE_COUNT,
  }
}

// Save shuffle state to localStorage
function saveShuffleState(state: ShuffleState): void {
  try {
    localStorage.setItem(SHUFFLE_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Ignore storage errors
  }
}

// Get next image from shuffle queue (no repeats until all shown)
function getNextImage(exclude?: string): string {
  const state = getShuffleState()

  // If queue is empty or only has the excluded image, reshuffle
  if (state.queue.length === 0 || (state.queue.length === 1 && state.queue[0] === exclude)) {
    state.queue = shuffleArray(BACKGROUND_IMAGES)
  }

  // Find first image that isn't excluded
  let imageIndex = 0
  if (exclude) {
    imageIndex = state.queue.findIndex(img => img !== exclude)
    if (imageIndex === -1) imageIndex = 0
  }

  // Remove and return the image
  const [image] = state.queue.splice(imageIndex, 1)
  saveShuffleState(state)

  return image
}

// Load credit metadata from txt file
async function loadCredit(imagePath: string): Promise<CreditInfo | null> {
  try {
    const txtPath = imagePath.replace('.webp', '.txt')
    const response = await fetch(txtPath)
    if (!response.ok) return null

    const text = await response.text()
    const lines = text.split('\n')
    return {
      description: lines[0]?.trim() || '',
      url: lines[1]?.trim() || '',
    }
  } catch {
    return null
  }
}

export function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Two background layers that crossfade
  const [images, setImages] = useState(() => {
    const first = getNextImage()
    const second = getNextImage(first)
    return [first, second]
  })
  const [activeIndex, setActiveIndex] = useState(0)
  const imagesRef = useRef(images)
  imagesRef.current = images

  // Credit metadata for each background layer
  const [credits, setCredits] = useState<[CreditInfo | null, CreditInfo | null]>([null, null])
  const [creditVisible, setCreditVisible] = useState(false)

  // Get the page user was trying to access
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  // Redirect if already authenticated
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [auth.isAuthenticated, navigate, from])

  // Load initial credits on mount
  useEffect(() => {
    // Load credits for initial images
    images.forEach((img, idx) => {
      loadCredit(img).then(credit => {
        setCredits(prev => {
          const next = [...prev] as [CreditInfo | null, CreditInfo | null]
          next[idx] = credit
          return next
        })
      })
    })

    // Show credit after brief delay
    const initialTimeout = setTimeout(() => setCreditVisible(true), 800)
    return () => clearTimeout(initialTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Background image rotation with animation choreography
  useEffect(() => {
    const interval = setInterval(() => {
      // Phase 1: Fade out credit
      setCreditVisible(false)

      // Phase 2: After credit fades, start image crossfade
      setTimeout(() => {
        setActiveIndex(prev => (prev === 0 ? 1 : 0))
      }, CREDIT_FADE_MS)

      // Phase 3: After image settles, show new credit
      setTimeout(() => {
        setCreditVisible(true)
      }, CREDIT_FADE_MS + IMAGE_CROSSFADE_MS + CREDIT_SETTLE_DELAY_MS)
    }, ROTATION_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  // When active index changes, preload next image into the now-hidden layer
  useEffect(() => {
    const hiddenIndex = activeIndex === 0 ? 1 : 0

    // Wait for transition to complete, then load next image into hidden layer
    const timeout = setTimeout(async () => {
      const currentVisibleImage = imagesRef.current[activeIndex]
      const nextImage = getNextImage(currentVisibleImage)

      // Preload image and fetch credit in parallel
      const img = new Image()
      img.src = nextImage

      const [, credit] = await Promise.all([
        new Promise<void>(resolve => {
          img.onload = () => resolve()
          img.onerror = () => resolve()
        }),
        loadCredit(nextImage),
      ])

      setImages(prev => {
        const newImages = [...prev]
        newImages[hiddenIndex] = nextImage
        return newImages
      })

      setCredits(prev => {
        const next = [...prev] as [CreditInfo | null, CreditInfo | null]
        next[hiddenIndex] = credit
        return next
      })
    }, IMAGE_CROSSFADE_MS + CREDIT_SETTLE_DELAY_MS)

    return () => clearTimeout(timeout)
  }, [activeIndex])

  const handleLogin = () => {
    auth.signinRedirect()
  }

  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background layers - two layers that alternate */}
      {images.map((image, index) => (
        <div
          key={index}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${image})`,
            opacity: activeIndex === index ? 1 : 0,
            transition: `opacity ${IMAGE_CROSSFADE_MS}ms ease-in-out`,
          }}
        />
      ))}

      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Credit overlay - bottom-left pill */}
      {credits[activeIndex] && credits[activeIndex]!.description && (
        <a
          href={credits[activeIndex]!.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`
            fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-lg z-10
            flex items-center gap-3
            bg-black/50 backdrop-blur-sm rounded-lg
            px-4 py-2.5
            hover:bg-black/60 group
            transition-opacity ease-in-out
            ${creditVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
          style={{ transitionDuration: `${CREDIT_FADE_MS}ms` }}
        >
          <p className="flex-1 text-white text-sm line-clamp-2 drop-shadow-sm">
            {credits[activeIndex]!.description}
          </p>
          <ExternalLink className="h-4 w-4 text-white/50 group-hover:text-white flex-shrink-0 transition-colors" />
        </a>
      )}

      {/* Content */}
      <Card className="w-full max-w-md relative z-20 backdrop-blur-sm bg-white/95 dark:bg-slate-800/95">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/logo.svg"
              alt="PBS SoCal"
              className="h-12 dark:hidden"
            />
            <img
              src="/logo-dark.svg"
              alt="PBS SoCal"
              className="h-12 hidden dark:block"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Data Platform
          </h1>
          <p className="mt-2 text-slate-500 dark:text-slate-400">
            Sign in to access the operations portal
          </p>
        </div>

        {auth.error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {auth.error.message}
          </div>
        )}

        <Button onClick={handleLogin} className="w-full" size="lg">
          Sign in with SSO
        </Button>

        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Protected by Keycloak Single Sign-On
        </p>
      </Card>
    </div>
  )
}
